import _ from "lodash";
import {
  IUser,
  ICertificateInfo,
  IUserProgramCertificate,
  IUserProgramRoles,
  ProgramData,
  AWSAccessParams,
  OnboardingCertificateIssueDate,
  IOptionalStandard,
  ProgramRole,
  IUserProgramRolesComplianceData,
} from "@interfaces/Interfaces";
import AWSService from "@controllers/AWSService";
import * as DatabaseService from "@controllers/DatabaseService";
import * as FirebaseService from "@controllers/FirebaseService";
import * as ProgramUtils from "@utils/programUtils";

/**
 * Interface for save details parameters
 */
export interface SaveDetailsParams {
  user: IUser;
  program: ProgramData;
  selectedPrimaryRoles: string[];
  selectedSecondaryRoles: string[];
  selectedStandards: IOptionalStandard[];
  selectedArea: string;
  selectedTeam: string;
  appraisalDate: Date | null;
  uploads: ICertificateInfo[];
  deletions: ICertificateInfo[];
  issueDates: OnboardingCertificateIssueDate[];
  expiryDates: OnboardingCertificateIssueDate[];
  nqpCourse?: string;
  startDate?: Date | null;
  complianceData?: IUserProgramRolesComplianceData[];
}

/**
 * Delete certificates from AWS
 */
export const deleteCertificate = async (user: IUser, deletions: ICertificateInfo[]): Promise<boolean | void> => {
  const toDelete = _.cloneDeep(deletions);
  const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user);

  for (let i = 0; i < toDelete.length; i++) {
    const file = toDelete[i];

    if (accessParams) {
      await AWSService.deleteCertificate(user, accessParams, file);
    }
  }

  return true;
};

/**
 * Upload certificates to AWS
 */
export const uploadCertificates = async (user: IUser, uploads: ICertificateInfo[]): Promise<ICertificateInfo[]> => {
  const toUpload = _.cloneDeep(uploads);
  const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user);

  const uploaded = [];

  for (let i = 0; i < toUpload.length; i++) {
    let file = await AWSService.uploadCertificate(user, accessParams, toUpload[i]);

    if (file && file.networkIssue) {
      file = await AWSService.uploadCertificateFromServer(user, accessParams, toUpload[i]);
    }

    const updatedFile: ICertificateInfo = {
      ...toUpload[i],
      content: undefined,
      url: file.Location,
    };

    delete updatedFile.content;

    uploaded.push(updatedFile);
  }

  return uploaded;
};

/**
 * Process certificate deletions
 */
const processCertificateDeletions = async (
  params: SaveDetailsParams,
  userProgramCertificates: IUserProgramCertificate[]
): Promise<IUserProgramCertificate[]> => {
  if (params.deletions.length > 0) {
    const deleted = await deleteCertificate(params.user, params.deletions);

    if (deleted) {
      for (let i = 0; i < params.deletions.length; i++) {
        const certificate = params.deletions[i];

        const index = userProgramCertificates?.findIndex(
          (item) => item.certificate?.certificateType === certificate.certificateType
        );

        if (index > -1) {
          userProgramCertificates.splice(index, 1);
        }
      }
    }
  }

  return userProgramCertificates;
};

/**
 * Process certificate uploads
 */
const processCertificateUploads = async (
  params: SaveDetailsParams,
  userProgramCertificates: IUserProgramCertificate[]
): Promise<IUserProgramCertificate[]> => {
  if (params.uploads.length > 0) {
    const uploaded = await uploadCertificates(params.user, params.uploads);

    await FirebaseService.logEvent("program_certificates_uploaded", {
      certificates_uploaded: uploaded.length,
      program: params.program.Name,
    });

    if (uploaded) {
      for (let i = 0; i < uploaded.length; i++) {
        const certificate = uploaded[i];
        const issueDate = params.issueDates.find((item) => item.type === certificate.certificateType);
        const expiryDate = params.expiryDates.find((item) => item.type === certificate.certificateType);

        const _certificate: IUserProgramCertificate = {
          certificate,
          ...(issueDate && { issueDate: issueDate.date.toISOString() }),
          ...(expiryDate && {
            expiryDate: expiryDate.date.toISOString(),
          }),
          programData: {
            programName: params.program.Name,
            certificateType: certificate.certificateType,
          },
        };

        const index = userProgramCertificates?.findIndex(
          (item) => item.certificate?.certificateType === _certificate.certificate?.certificateType
        );

        if (index > -1) {
          userProgramCertificates.splice(index, 1, _certificate);
        } else {
          userProgramCertificates.push(_certificate);
        }
      }
    }
  }

  return userProgramCertificates;
};

/**
 * Update existing certificate dates
 */
const updateExistingCertificateDates = (
  params: SaveDetailsParams,
  userProgramCertificates: IUserProgramCertificate[]
): IUserProgramCertificate[] => {
  if (params.uploads.length === 0) {
    // Update issue dates
    if (params.issueDates.length > 0) {
      for (let i = 0; i < params.issueDates.length; i++) {
        const issueDate = params.issueDates[i];

        const certificate: IUserProgramCertificate | undefined = userProgramCertificates?.find(
          (item) => item.certificate?.certificateType === issueDate.type
        );
        const index = userProgramCertificates?.findIndex(
          (item) => item.certificate?.certificateType === issueDate.type
        );

        if (certificate) {
          const _certificate: IUserProgramCertificate = {
            ...certificate,
            issueDate: issueDate.date.toISOString(),
          };

          if (index > -1) {
            userProgramCertificates.splice(index, 1, _certificate);
          }
        }
      }
    }

    // Update expiry dates
    if (params.expiryDates.length > 0) {
      for (let i = 0; i < params.expiryDates.length; i++) {
        const expiryDate = params.expiryDates[i];

        const certificate: IUserProgramCertificate | undefined = userProgramCertificates?.find(
          (item) => item.certificate?.certificateType === expiryDate.type
        );
        const index = userProgramCertificates?.findIndex(
          (item) => item.certificate?.certificateType === expiryDate.type
        );

        if (certificate) {
          const _certificate: IUserProgramCertificate = {
            ...certificate,
            expiryDate: expiryDate.date.toISOString(),
          };

          if (index > -1) {
            userProgramCertificates.splice(index, 1, _certificate);
          }
        }
      }
    }
  }

  return userProgramCertificates;
};

/**
 * Create user roles object
 */
const createUserRoles = (params: SaveDetailsParams): IUserProgramRoles => {
  const _userRoles: IUserProgramRoles = {
    programName: params.program.Name,
    programID: params.program.ID,
    programRoles: params.selectedPrimaryRoles,
    ...(params.selectedArea.length > 0 && { area: params.selectedArea }),
    ...(params.selectedTeam.length > 0 && { team: params.selectedTeam }),
    ...(params.appraisalDate && { appraisalDate: params.appraisalDate.toISOString() }),
    ...(params.startDate && { startDate: params.startDate.toISOString() }),
    ...(params.nqpCourse && { courseId: params.nqpCourse }),
    ...(params.complianceData && { complianceData: params.complianceData }),
  };

  if (ProgramUtils.saveUserDetailsForEPRRProgram(params.program?.ID)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const role = params.program.Roles?.find((r: ProgramRole) => r.Name === params.selectedPrimaryRoles[0])!;
    _userRoles.programRoles = [
      {
        roleID: role.ID,
        roleName: params.selectedPrimaryRoles[0],
        optionalStandards: params.selectedStandards.map((standard) => ({
          id: standard.id,
          name: standard.name,
        })),
      },
    ];
    _userRoles.programID = params.program?.ID;
    _userRoles.area = params.selectedArea;
    if (ProgramUtils.showDeclarationOfObligationsTickbox(params.program.ID)) {
      _userRoles.declarationTickDate = new Date().toISOString();
    }

    FirebaseService.logEvent("optional_standards_confirmed", {
      standards: params.selectedStandards.map((standard) => standard.name),
    });
  }

  const secondaryRoleDefinitions = [
    ...(params.program.Roles?.find((r: ProgramRole) => r.Name === params.selectedPrimaryRoles[0])
      ?.MandatorySecondaryRoles ?? []),
    ...(params.program.Roles?.find((r: ProgramRole) => r.Name === params.selectedPrimaryRoles[0])
      ?.OptionalSecondaryRoles ?? []),
  ];

  if (ProgramUtils.saveUserDetailsForEPRRProgram(params.program?.ID)) {
    _userRoles.secondaryRoles = params.selectedSecondaryRoles.map((roleName) => {
      const role = secondaryRoleDefinitions.find((r: ProgramRole) => r.Name === roleName)!;
      return {
        roleID: role.ID,
        roleName,
      };
    });
  } else {
    _userRoles.secondaryRoles = params.selectedSecondaryRoles;
  }

  return _userRoles;
};

/**
 * Main save details function
 */
export const saveDetails = async (params: SaveDetailsParams): Promise<IUser> => {
  const copiedParams = structuredClone(params);

  const userToSave = _.cloneDeep(params.user);
  let userProgramCertificates = _.cloneDeep(userToSave.programCertificates) || [];

  // Process certificate deletions
  userProgramCertificates = await processCertificateDeletions(copiedParams, userProgramCertificates);

  // Process certificate uploads
  userProgramCertificates = await processCertificateUploads(copiedParams, userProgramCertificates);

  // Update existing certificate dates
  userProgramCertificates = updateExistingCertificateDates(copiedParams, userProgramCertificates);

  userToSave.programCertificates = userProgramCertificates;

  if (ProgramUtils.isNwasARC(copiedParams.program.ID) || ProgramUtils.isSPAPRP(copiedParams.program.ID) || ProgramUtils.isSwastEPRR(copiedParams.program.ID) || ProgramUtils.isNwasEPRR(copiedParams.program.ID)) {
    if (!copiedParams.startDate) {
      copiedParams.startDate = new Date();
    }
  }

  // Handle user roles
  const userRoles = userToSave.programRoles || [];
  const _userRoles = createUserRoles(copiedParams);

  const index = userRoles?.findIndex((item) => item.programName === copiedParams.program.Name);

  if (index > -1) {
    userRoles.splice(index, 1, _userRoles);
  } else {
    userRoles.push(_userRoles);
  }

  userToSave.programRoles = userRoles;

  // Save to database
  await DatabaseService.updateUserDetails(userToSave, {
    programRoles: userToSave.programRoles,
    programCertificates: userToSave.programCertificates,
  });

  // Log analytics
  if (copiedParams.selectedPrimaryRoles.length > 0) {
    FirebaseService.logEvent("program_roles_updated", {
      program: copiedParams.program?.Name,
      roles: JSON.stringify(copiedParams.selectedPrimaryRoles),
    });
  }

  return userToSave;
};
